import { format } from 'date-fns'
import { useClients } from '@/hooks/queries/useClients'
import { useWasteSubcategories } from '@/hooks/queries/useWasteSubcategories'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Combobox } from '@/components/ui/combobox'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import type { DashboardFilters } from '@/types'

interface Props {
  value: DashboardFilters
  onChange: (next: DashboardFilters) => void
}

const currentMonth = format(new Date(), 'yyyy-MM')
const prevMonth = format(new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1), 'yyyy-MM')

export function DashboardFiltersBar({ value, onChange }: Props) {
  const { data: clients = [] } = useClients()
  const { data: subcategories = [] } = useWasteSubcategories()

  function set(patch: Partial<DashboardFilters>) {
    onChange({ ...value, ...patch })
  }

  function reset() {
    onChange({ period: currentMonth })
  }

  return (
    <div className="flex flex-wrap items-end gap-4">
      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">Período</Label>
        <Select
          value={value.period ?? currentMonth}
          onValueChange={(v) => set({ period: v, date_from: undefined, date_to: undefined })}
        >
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Seleccionar período" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={currentMonth}>Mes actual</SelectItem>
            <SelectItem value={prevMonth}>Mes anterior</SelectItem>
            <SelectItem value={`${new Date().getFullYear()}-Q1`} disabled>Trimestre (próx.)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">Cliente</Label>
        <Combobox
          options={[
            { value: 'all', label: 'Todos los clientes' },
            ...clients.map((c) => ({ value: String(c.id), label: c.trade_name })),
          ]}
          value={value.client_id ? String(value.client_id) : 'all'}
          onChange={(v) => set({ client_id: v === 'all' ? undefined : Number(v) })}
          placeholder="Todos los clientes"
          className="w-52"
          contentClassName="w-52"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">Subcategoría de residuo</Label>
        <Select
          value={value.waste_subcategory_id ? String(value.waste_subcategory_id) : 'all'}
          onValueChange={(v) => set({ waste_subcategory_id: v === 'all' ? undefined : Number(v) })}
        >
          <SelectTrigger className="w-52">
            <SelectValue placeholder="Todas las subcategorías" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las subcategorías</SelectItem>
            {subcategories.map((sc) => (
              <SelectItem key={sc.id} value={String(sc.id)}>
                {sc.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Button variant="outline" size="sm" onClick={reset}>
        Resetear filtros
      </Button>
    </div>
  )
}
